import dayjsTZ, { dayjsInTz } from "../../../utils/dayjs";
import { getCompanyTimezone } from "../../../utils/timezone/teamTimezone";
import { getDayTypeTranslations } from "../../Reports/processFactory/approvals/approvals";

const HOUR_MS = 3600000;

const FALLBACK_ABSENCE_TYPES = [
  "HolidayRequest",
  "NationalHoliday",
  "NationalHolidayWorking",
  "Sick",
  "Maternity",
];

const WORK_DAY_TYPES = new Set(["Regular", "MUNGESE"]);

const overlapHours = (startMs, endMs, rangeStart, rangeEnd) => {
  const s = Math.max(startMs, rangeStart);
  const e = Math.min(endMs, rangeEnd);
  return Math.max(0, (e - s) / HOUR_MS);
};

/**
 * Split a shift into wall-clock bands:
 * - before 19:00
 * - 19:00–22:00
 * - after 22:00
 */
export const splitShiftIntoBands = (
  clockInDate,
  clockOutDate,
  timezone = getCompanyTimezone()
) => {
  if (!clockInDate || !clockOutDate) {
    return {
      before19: 0,
      between19and22: 0,
      after22: 0,
      total: 0,
    };
  }

  const d = (v) => (timezone ? dayjsInTz(timezone, v) : dayjsTZ(v));
  const startMs = d(clockInDate).valueOf();
  const endMs = d(clockOutDate).valueOf();
  if (!(endMs > startMs)) {
    return { before19: 0, between19and22: 0, after22: 0, total: 0 };
  }

  let before19 = 0;
  let between19and22 = 0;
  let after22 = 0;

  let day = d(clockInDate).startOf("day");
  const lastDay = d(clockOutDate).startOf("day");

  while (day.valueOf() <= lastDay.valueOf()) {
    const dayStart = day.valueOf();
    const nineteen = day.hour(19).minute(0).second(0).millisecond(0).valueOf();
    const twentyTwo = day.hour(22).minute(0).second(0).millisecond(0).valueOf();
    const nextDay = day.add(1, "day").startOf("day").valueOf();

    before19 += overlapHours(startMs, endMs, dayStart, nineteen);
    between19and22 += overlapHours(startMs, endMs, nineteen, twentyTwo);
    after22 += overlapHours(startMs, endMs, twentyTwo, nextDay);

    day = day.add(1, "day");
  }

  const round = (n) => Number(n.toFixed(2));
  return {
    before19: round(before19),
    between19and22: round(between19and22),
    after22: round(after22),
    total: round(before19 + between19and22 + after22),
  };
};

/** Absence / leave / holiday types from ClockIn Types programFields options. */
export const getAbsenceDayTypes = (fieldOptions = []) => {
  const fromOptions = (fieldOptions || [])
    .map((el) => el?.name)
    .filter((name) => !!name && !WORK_DAY_TYPES.has(name));

  return new Set(fromOptions.length ? fromOptions : FALLBACK_ABSENCE_TYPES);
};

export const isLeaveOrHolidayType = (dayType, absenceTypes) => {
  if (!dayType) return false;
  if (absenceTypes instanceof Set) return absenceTypes.has(dayType);
  return FALLBACK_ABSENCE_TYPES.includes(dayType);
};

/** Leaves / holidays / sick / maternity overlapping the calendar day. */
export const getDayLeaveItems = ({
  clockInDate,
  leaves = [],
  clockings = [],
  fieldOptions = [],
  dayTypeLabels = {},
}) => {
  if (!clockInDate) return [];

  const absenceTypes = getAbsenceDayTypes(fieldOptions);
  const dayStart = dayjsTZ(clockInDate).startOf("day");
  const dayEnd = dayjsTZ(clockInDate).endOf("day");

  const overlapsDay = (item) => {
    const start = dayjsTZ(item?.clockInDate || item?.requestStartDate);
    const end = dayjsTZ(
      item?.clockOutDate ||
        item?.requestEndDate ||
        item?.clockInDate ||
        item?.requestStartDate
    );
    if (!start.isValid()) return false;
    const endSafe = end.isValid() ? end : start;
    return (
      start.valueOf() <= dayEnd.valueOf() &&
      endSafe.valueOf() >= dayStart.valueOf()
    );
  };

  const seen = new Set();
  const items = [];

  const labelFor = (dayType) =>
    dayTypeLabels[dayType] ||
    getDayTypeTranslations(dayType || "") ||
    dayType ||
    "";

  const pushItem = (item, source) => {
    if (!isLeaveOrHolidayType(item?.dayType, absenceTypes)) return;

    const id =
      item?.clockingId || item?.requestId || `${source}-${item?.clockInDate}`;
    if (seen.has(id)) return;
    seen.add(id);

    items.push({
      id,
      dayType: item?.dayType,
      label: labelFor(item?.dayType),
      start: item?.clockInDate || item?.requestStartDate,
      end: item?.clockOutDate || item?.requestEndDate,
      approved: item?.approved,
      note: item?.clockInNote || item?.requestDescription || "",
      source,
      color:
        (fieldOptions || []).find((opt) => opt?.name === item?.dayType)
          ?.color || null,
    });
  };

  (leaves || []).forEach((el) => {
    if (overlapsDay(el)) pushItem(el, "leave");
  });

  (clockings || []).forEach((el) => {
    if (overlapsDay(el)) pushItem(el, "clocking");
  });

  return items.sort((a, b) => (a.start || 0) - (b.start || 0));
};
